import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'

import NavigationItem from 'components_new/molecules/NavigationItem'
import SidebarDialog from 'components_new/molecules/SidebarDialog'

import Actions from './Actions'
import Content, { TNewSourceValues } from './Content'
import Hero from './Hero'
import { INITIAL_AUTH_VALIDATION, TAuthValidation } from './Content/AuthStep'

import {
  buildSteps,
  ESourceStep,
  formatCreateBody,
  formatValidateBody,
  TSelectedSource
} from './lib'

import * as AuthParamValuesApi from 'redux/api/DataPlatform/connections/authParamValues/v1'
import { useActiveSourcesActions } from 'redux/actions/DataPlatform/connections/ActiveSources'
import { useSources, useSourcesCreating } from 'redux/hooks/Sources'

import {
  CREATABLE_SOURCE_CATEGORIES,
  ESourceCategory,
  SOURCE_CATEGORY_META
} from 'utils/sources'

const INITIAL_VALUES: TNewSourceValues = {
  authParamValues: {},
  customAuth: { field: '', location: '', value: '' },
  displayName: ''
}

interface NewSourceDialogProps {
  onClose: () => void
  open: boolean
}

const NewSourceDialog = (props: NewSourceDialogProps) => {
  const { onClose, open } = props

  const [, sources] = useSources()
  const creating = useSourcesCreating()
  const { tryCreateActiveSource } = useActiveSourcesActions()

  const [category, setCategory] = useState<ESourceCategory>(
    ESourceCategory.SYSTEM
  )
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<TSelectedSource | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [values, setValues] = useState<TNewSourceValues>(INITIAL_VALUES)
  const [validation, setValidation] = useState<TAuthValidation>(
    INITIAL_AUTH_VALIDATION
  )

  useEffect(() => {
    if (open) {
      setCategory(ESourceCategory.SYSTEM)
      setSearch('')
      setSelected(null)
      setActiveStep(0)
      setValues(INITIAL_VALUES)
      setValidation(INITIAL_AUTH_VALIDATION)
    }
  }, [open])

  const steps = buildSteps(category, selected)
  const currentStep = steps[activeStep]

  const duplicateName = Object.values(sources)
    .filter((source) => source.included && source.included.active_sources)
    .some((source) =>
      source.included.active_sources.some(
        (activeSource) =>
          activeSource.attributes.display_name &&
          activeSource.attributes.display_name.toLowerCase() ===
            values.displayName.trim().toLowerCase()
      )
    )

  /** Back out of the first step and you are picking a source again. */
  const handleChangeStep = (step: number) => {
    if (step < 0) {
      setSelected(null)
      setActiveStep(0)
      setValues(INITIAL_VALUES)
      setValidation(INITIAL_AUTH_VALIDATION)
    } else {
      setActiveStep(step)
    }
  }

  const handleSelect = (value: TSelectedSource) => {
    setSelected(value)
    setActiveStep(0)
    setValidation(INITIAL_AUTH_VALIDATION)
    setValues({
      ...INITIAL_VALUES,
      // The picked database card decides which engine to connect to.
      authParamValues: value.databaseType
        ? { database_type: value.databaseType }
        : {},
      displayName: value.title
    })
  }

  const handleChangeValue = <K extends keyof TNewSourceValues>(
    field: K,
    value: TNewSourceValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }))

    // Any change to the credentials invalidates the previous check.
    if (field === 'authParamValues') {
      setValidation(INITIAL_AUTH_VALIDATION)
    }
  }

  const handleValidateAuth = () => {
    if (!selected) {
      return
    }

    setValidation({ error: false, success: false, validating: true })

    AuthParamValuesApi.validate(
      formatValidateBody(
        selected.source.id,
        values.authParamValues,
        selected.source.included.auth_params
      )
    )
      .then(() =>
        setValidation({ error: false, success: true, validating: false })
      )
      .catch(() =>
        setValidation({ error: true, success: false, validating: false })
      )
  }

  const handleCreate = () => {
    if (!selected) {
      return
    }

    const { source } = selected
    const displayName = values.displayName.trim()

    if (source.attributes.is_custom_api) {
      tryCreateActiveSource({
        data: {
          attributes: {
            auth_params: [values.customAuth],
            display_name: displayName,
            source_id: source.id
          }
        }
      })
    } else {
      tryCreateActiveSource(
        formatCreateBody(
          source.id,
          values.authParamValues,
          source.included.auth_params,
          displayName
        )
      )
    }

    onClose()
  }

  const nextDisabled =
    creating ||
    (currentStep === ESourceStep.AUTH && !validation.success) ||
    (currentStep === ESourceStep.NAME &&
      (values.displayName.trim() === '' || duplicateName))

  return (
    <SidebarDialog
      actions={
        <Actions
          activeStep={activeStep}
          creating={creating}
          nextDisabled={nextDisabled}
          onChangeStep={handleChangeStep}
          onClose={onClose}
          onCreate={handleCreate}
          selected={Boolean(selected)}
          stepsCount={steps.length}
        />
      }
      fullHeight={true}
      fullWidth={true}
      hero={
        <Hero
          activeStep={activeStep}
          search={search}
          selected={Boolean(selected)}
          setSearch={setSearch}
          steps={steps}
        />
      }
      leftSidebar={
        selected
          ? undefined
          : {
              content: (
                <Box>
                  {CREATABLE_SOURCE_CATEGORIES.map((item) => (
                    <NavigationItem
                      active={category === item}
                      item={{
                        icon: { name: SOURCE_CATEGORY_META[item].icon },
                        onClick: () => {
                          setCategory(item)
                          setSearch('')
                        },
                        title: SOURCE_CATEGORY_META[item].label
                      }}
                      key={item}
                    />
                  ))}
                </Box>
              ),
              size: 'small'
            }
      }
      maxWidth={selected ? 'md' : 'lg'}
      onClose={onClose}
      open={open}
      title="Lägg till datakälla"
    >
      <Content
        category={category}
        currentStep={currentStep}
        duplicateName={duplicateName}
        onChange={handleChangeValue}
        onSelect={handleSelect}
        onValidateAuth={handleValidateAuth}
        search={search}
        selected={selected}
        sources={sources}
        validation={validation}
        values={values}
      />
    </SidebarDialog>
  )
}

export default NewSourceDialog
